import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDropzone } from "react-dropzone";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import styles from "./Fileupload.module.css";

const Fileupload = ({ onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const ALLOWED_TYPES = ["application/pdf", "text/plain"];
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 20;

  const handleFileValidation = (newFiles) => {
    if (files.length + newFiles.length > MAX_FILES) {
      setError(`You can upload a maximum of ${MAX_FILES} files.`);
      return false;
    }

    for (const file of newFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(
          `Invalid file type "${file.name}". Only PDF and text files are allowed.`
        );
        return false;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(
          `File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`
        );
        return false;
      }
    }
    setError("");
    return true;
  };

  const handleDrop = (acceptedFiles) => {
    if (handleFileValidation(acceptedFiles)) {
      const updatedFiles = acceptedFiles.map((file) => ({
        file,
        progress: 0,
        status: "Pending",
      }));
      setFiles((prev) => [...prev, ...updatedFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (fileObj, index) => {
    // Commented out the actual API call to skip posting to server
    /*
    const formData = new FormData();
    formData.append("files", fileObj.file);
    */

    try {
      /*
      const response = await axios.post(
        process.env.REACT_APP_SERVER_API,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setFiles((prev) => {
              const updatedFiles = [...prev];
              updatedFiles[index].progress = progress;
              return updatedFiles;
            });
          },
        }
      );
      */

      // Simulating a successful upload locally
      setFiles((prev) => {
        const updatedFiles = [...prev];
        updatedFiles[index].progress = 100;
        updatedFiles[index].status = "Uploaded";
        return updatedFiles;
      });

      toast.success(`${fileObj.file.name} uploaded successfully!`, {
        position: "top-right",
        autoClose: 1500,
      });

      // Add delay after toast
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (err) {
      console.error("File upload failed:", err);
      setFiles((prev) => {
        const updatedFiles = [...prev];
        updatedFiles[index].status = "Failed";
        return updatedFiles;
      });

      toast.error(`Failed to upload ${fileObj.file.name}. Please try again.`, {
        position: "top-right",
        autoClose: 2000,
      });

      // Add delay after error toast
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  };

  const handleUploadFiles = async () => {
    if (files.length === 0) {
      setError("No files selected for upload.");
      return;
    }

    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      if (files[i].status === "Pending") {
        await uploadFile(files[i], i);
      }
    }

    setIsUploading(false);
    onUploadComplete?.();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: ALLOWED_TYPES.join(","),
    maxFiles: MAX_FILES,
  });

  return (
    <Box className={styles.fileUploadContainer}>
      <Typography variant="h5" className={styles.header}>
        File Upload
      </Typography>
      <Typography variant="body2" className={styles.subHeader}>
        Max 5 files, each up to 20MB. Only PDF and text files are allowed.
      </Typography>

      <Box
        {...getRootProps()}
        className={`${styles.dropzone} ${
          isDragActive ? styles.activeDropzone : ""
        }`}
      >
        <input {...getInputProps()} />
        <Typography variant="body2" className={styles.dropzoneText}>
          {isDragActive
            ? "Drop files here..."
            : "Drag & Drop files here or click to select"}
        </Typography>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        className={styles.actionButtons}
      >
        <Button
          variant="contained"
          color="inherit"
          component="label"
          startIcon={<UploadFileIcon />}
          className={styles.selectButton}
          disabled={files.length >= MAX_FILES}
        >
          {files.length < MAX_FILES ? "Select Files" : "Limit Reached"}
          <input
            type="file"
            multiple
            hidden
            onChange={(e) => handleDrop(Array.from(e.target.files))}
            accept={ALLOWED_TYPES.join(",")}
          />
        </Button>
        <Button
          variant="contained"
          color="inherit"
          startIcon={<CloudUploadIcon />}
          className={styles.selectButton}
          onClick={handleUploadFiles}
          disabled={files.length === 0 || isUploading}
        >
          {isUploading ? "Uploading..." : "Upload Files"}
        </Button>
      </Stack>

      {error && (
        <Typography variant="body2" color="error" className={styles.errorText}>
          {error}
        </Typography>
      )}

      <List className={styles.fileList}>
        {files.map((fileObj, index) => (
          <ListItem key={index} className={styles.fileListItem}>
            <ListItemText
              primary={fileObj.file.name}
              secondary={
                fileObj.status === "Uploading"
                  ? `${(fileObj.file.size / (1024 * 1024)).toFixed(
                      2
                    )} MB - Uploading...`
                  : `${(fileObj.file.size / (1024 * 1024)).toFixed(2)} MB - ${
                      fileObj.status
                    }`
              }
            />
            {fileObj.status === "Uploading" && (
              <LinearProgress
                variant="determinate"
                value={fileObj.progress}
                className={styles.progressBar}
              />
            )}
            <IconButton
              onClick={() => handleRemoveFile(index)}
              disabled={fileObj.status === "Uploading"}
              className={styles.deleteButton}
            >
              <DeleteIcon />
            </IconButton>
          </ListItem>
        ))}
      </List>

      <ToastContainer />
    </Box>
  );
};

export default Fileupload;
